import {
    Box,
    Button,
    Chip,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ArrowUpRight, Pencil, Search, ShieldCheck, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import BooleanPill from '../../component/common/BooleanPill';
import DialogForm from '../../component/dialog/DialogForm';
import FilterToolbar from '../../component/filter/FilterToolbar';
import NewItemButton from '../../component/common/NewItemButton';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import usePermissions from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileCrudList from '../../component/common/MobileCrud';
import { protoIcon } from '../../component/common/ProtoIcon';

const ShieldIcon = protoIcon('shield');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');
// Tons do ícone da linha (azul, verde, roxo, âmbar) como no protótipo.
const ROW_TONES = [undefined, 'green', 'purple', 'amber'];

// Filtro aplicado só ao clicar "Buscar"/Enter — mesma técnica de
// `users/List.js`/`vehicles/checklist/List.js` (report-toolbar §6.1): o
// estado do campo (`search`) é distinto do filtro efetivamente aplicado
// (`appliedSearch`), que alimenta o chip removível.
function applyProfileFilters(list, searchValue) {
    let result = Array.isArray(list) ? [...list] : [];
    if (searchValue && searchValue.trim()) {
        const term = searchValue.trim().toLowerCase();
        result = result.filter((item) => (item?.name || '').toLowerCase().includes(term));
    }
    return result;
}

// Regra de bloqueio de exclusão (prototipo-lista-perfis.html, mesmo padrão
// "grupo em uso" de `vehicles/checklist/ListInspGroup.js` e "template
// padrão" de `vehicles/checklist/List.js`): um perfil de Sistema (hoje só
// "Administrador", `is_admin`) nunca pode ser excluído; um perfil
// Personalizado só pode ser excluído se não estiver atribuído a nenhum
// usuário. `usageCount` é calculado no cliente a partir de `GET /users`
// (contagem por `profile_id`) — a API de perfis (`GET /profiles`) não expõe
// essa contagem hoje.
function deleteBlockReason(profile) {
    if (profile?.is_admin) return 'Não é possível excluir um perfil padrão do sistema.';
    const count = profile?.usageCount || 0;
    if (count > 0) {
        return `Não é possível excluir — este perfil está em uso por ${count} usuário${count === 1 ? '' : 's'}. Reatribua-os a outro perfil antes de excluir.`;
    }
    return null;
}

export default function List() {
    const theme = useTheme();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();

    const [profiles, setProfiles] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);

    // Valor do campo de busca (não aplicado ainda) vs. filtro efetivamente
    // aplicado (alimenta o chip removível).
    const [search, setSearch] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');

    const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
    const [profileToDelete, setProfileToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const handleShowSuccessSnackbar = (text) => {
        setSnackbarSeverity('success');
        setSnackbarMessage(text);
        setOpenSnackbar(true);
    };

    const handleShowErrorSnackbar = (errorMessage) => {
        const text = typeof errorMessage === 'string'
            ? errorMessage
            : errorMessage != null
                ? JSON.stringify(errorMessage)
                : 'Erro ao realizar operação';
        setSnackbarSeverity('error');
        setSnackbarMessage(text);
        setOpenSnackbar(true);
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const [profilesResponse, usersResponse] = await Promise.all([
                httpClient.get('/profiles'),
                httpClient.get('/users'),
            ]);
            const profilesData = Array.isArray(profilesResponse.data) ? profilesResponse.data : [];
            const usersData = Array.isArray(usersResponse.data) ? usersResponse.data : [];

            const usageByProfileId = usersData.reduce((acc, u) => {
                if (u?.profile_id) acc[u.profile_id] = (acc[u.profile_id] || 0) + 1;
                return acc;
            }, {});

            const withUsage = profilesData.map((p) => ({ ...p, usageCount: usageByProfileId[p.id] || 0 }));
            setProfiles(withUsage);
            setFilteredData(applyProfileFilters(withUsage, appliedSearch));
        } catch (error) {
            console.error('Error fetching profiles:', error);
            setProfiles([]);
            setFilteredData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleApplyFilters = () => {
        setAppliedSearch(search);
        setFilteredData(applyProfileFilters(profiles, search));
    };

    const handleClearFilters = () => {
        setSearch('');
        setAppliedSearch('');
        setFilteredData(profiles);
    };

    const handleRemoveSearchFilter = () => {
        setSearch('');
        setAppliedSearch('');
        setFilteredData(applyProfileFilters(profiles, ''));
    };

    const handleDeleteProfile = (row) => {
        const reason = deleteBlockReason(row);
        if (reason) {
            handleShowErrorSnackbar(reason);
            return;
        }
        setProfileToDelete(row);
        setOpenDialogConfirmDelete(true);
    };

    const confirmDelete = async () => {
        const name = profileToDelete?.name;
        try {
            setDeleting(true);
            await httpClient.delete(`/profiles/${profileToDelete.id}`);
            setOpenDialogConfirmDelete(false);
            setProfileToDelete(null);
            await loadData();
            handleShowSuccessSnackbar(`Perfil "${name}" excluído com sucesso!`);
        } catch (error) {
            console.error('Error deleting profile:', error);
            handleShowErrorSnackbar(error.response?.data || error.message || 'Erro ao excluir perfil');
        } finally {
            setDeleting(false);
        }
    };

    const columns = [
        {
            path: 'name',
            name: 'Nome',
            render: (row) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '10px',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: row.is_admin ? 'info.bg' : 'background.subtle',
                        border: row.is_admin ? 'none' : '1px solid',
                        borderColor: 'divider',
                        color: row.is_admin ? 'primary.main' : 'text.secondary',
                    }}>
                        <ShieldCheck size={15} strokeWidth={2} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                            {row.name}
                        </Typography>
                        <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '1px' }}>
                            {row.usageCount} usuário{row.usageCount === 1 ? '' : 's'} vinculado{row.usageCount === 1 ? '' : 's'}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            path: 'is_admin',
            name: 'Tipo',
            render: (row) => <BooleanPill value={!!row.is_admin} trueLabel="Sistema" falseLabel="Personalizado" />,
        },
    ];

    const actions = [
        ...(hasPermission('profile.update') ? [{
            icon: <Pencil size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => navigate(`/profiles/edit/${row.id}`),
                tooltip: 'Editar',
            },
        }] : []),
        ...(hasPermission('profile.delete') ? [{
            icon: (row) => <Trash2 size={16} strokeWidth={2} color={deleteBlockReason(row) ? theme.palette.text.disabled : theme.palette.error.main} />,
            props: {
                onClick: (row) => handleDeleteProfile(row),
                tooltip: 'Excluir',
            },
        }] : []),
    ];

    const deleteDialog = (
        <DialogForm
            iconType="warning"
            open={openDialogConfirmDelete}
            title="Atenção"
            confirmButtonText="Sim, Excluir"
            cancelButtonText="Cancelar"
            content={`Tem certeza que deseja excluir o perfil "${profileToDelete?.name || ''}"?`}
            secondaryContent="Esta ação não pode ser desfeita."
            onSubmit={confirmDelete}
            onClose={() => {
                setOpenDialogConfirmDelete(false);
                setProfileToDelete(null);
            }}
            loading={deleting}
            loadingText="Removendo..."
        />
    );

    // Shell mobile: lista do protótipo `sistema/perfis.html` (mesmos dados e ações).
    if (isMobileShell) {
        const handleMobileSearch = (value) => {
            setSearch(value);
            setAppliedSearch(value);
            setFilteredData(applyProfileFilters(profiles, value));
        };
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget="Perfis"
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileCrudList
                    items={filteredData}
                    getKey={(row) => row.id}
                    getRow={(row) => ({
                        icon: ShieldIcon,
                        tone: ROW_TONES[profiles.findIndex((p) => p.id === row.id) % ROW_TONES.length],
                        title: row.name,
                        sub: row.is_admin ? 'Perfil de sistema' : 'Perfil personalizado',
                        end: (
                            <Box component="span" sx={{ fontSize: 12, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                                {row.usageCount} usuário{row.usageCount === 1 ? '' : 's'}
                            </Box>
                        ),
                    })}
                    loading={loading}
                    search={search}
                    onSearchChange={handleMobileSearch}
                    searchPlaceholder="Buscar perfil…"
                    emptyIcon={ShieldIcon}
                    emptyTitle="Nenhum perfil encontrado"
                    emptyText="Ajuste a busca para ver outros perfis."
                    fabLabel={hasPermission('profile.create') ? 'Novo' : undefined}
                    onNew={() => navigate('/profiles/new')}
                    rowTitle={(row) => row.name}
                    rowActions={(row) => [
                        ...(hasPermission('profile.update') ? [{ label: 'Editar', icon: EditIcon, onClick: () => navigate(`/profiles/edit/${row.id}`) }] : []),
                        ...(hasPermission('profile.delete') ? [{ label: 'Excluir', icon: TrashIcon, destructive: true, onClick: () => handleDeleteProfile(row) }] : []),
                    ]}
                />
                {deleteDialog}
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget="Perfis"
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* page-head — ícone-badge + título + descrição + ação (Novo perfil). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 1,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <ShieldCheck size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Perfis
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Gerencie os perfis de permissão atribuídos aos usuários do sistema.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('profile.create') && (
                        <NewItemButton label="Novo perfil" onClick={() => navigate('/profiles/new')} />
                    )}
                </Box>

                {/* crosslink — referência cruzada para a Lista de usuários (prototipo-lista-perfis.html). */}
                <Typography sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '4px',
                    fontSize: '12.5px',
                    color: 'text.disabled',
                    mb: 2.5,
                }}>
                    Cada perfil listado aqui pode ser atribuído a um usuário na tela
                    <Box
                        component={RouterLink}
                        to="/users"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '3px',
                            color: 'primary.main',
                            fontWeight: 700,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        Usuários <ArrowUpRight size={11} strokeWidth={2.5} />
                    </Box>
                    .
                </Typography>

                {/* report-toolbar — só Buscar, mesmo template §6.1 usado nas demais listas simples. */}
                <FilterToolbar
                    plain
                    activeCount={appliedSearch ? 1 : 0}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 300px' }}>
                            <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
                                Buscar
                            </Typography>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Nome do perfil…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleApplyFilters(); }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: '9px' },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                />

                {/* chips-row — filtro ativo removível + contagem. */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2, rowGap: 1 }}>
                    {appliedSearch && (
                        <Chip
                            label={`Busca: "${appliedSearch}"`}
                            size="small"
                            onDelete={handleRemoveSearchFilter}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${filteredData.length} perfil${filteredData.length === 1 ? '' : 'is'} encontrado${filteredData.length === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                    />
                </Stack>

                <DefaultTable
                    colunms={columns}
                    data={filteredData}
                    iconsActions={actions}
                    loading={loading}
                    rowPerPage={10}
                    rowsPerPageOptions={[8, 10, 20]}
                    itemLabel="perfis"
                    sortableFields={['name']}
                    defaultSort={{ field: 'name', direction: 'asc' }}
                />
            </Box>

            <DialogForm
                iconType="warning"
                open={openDialogConfirmDelete}
                title="Atenção"
                confirmButtonText="Sim, Excluir"
                cancelButtonText="Cancelar"
                content={`Tem certeza que deseja excluir o perfil "${profileToDelete?.name || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita."
                onSubmit={confirmDelete}
                onClose={() => {
                    setOpenDialogConfirmDelete(false);
                    setProfileToDelete(null);
                }}
                loading={deleting}
                loadingText="Removendo..."
            />
        </PageScaffold>
    );
}
